/**
 * Popover (Task 3) — a small trigger-button + floating panel used by the toolbar
 * Filters and Column-settings controls. Click-outside and Esc close it (Esc
 * restores focus to the trigger). The trigger is a standard secondary Button with
 * an optional count badge; the panel is a role=dialog labelled by the trigger.
 * Token-driven; reuses the overlay surface + pop-in animation.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "./Button.js";
import type { IconName } from "./Icon.js";

export function Popover({
  label,
  icon,
  badge,
  align = "left",
  panelLabel,
  panelClassName,
  children,
}: {
  label: string;
  icon?: IconName;
  /** Optional count shown as a pill on the trigger (omitted when 0/undefined). */
  badge?: number;
  align?: "left" | "right";
  panelLabel?: string;
  panelClassName?: string;
  /** Panel content; receives a `close` callback to dismiss after an action. */
  children: ReactNode | ((close: () => void) => ReactNode);
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        rootRef.current?.querySelector<HTMLButtonElement>(".popover__trigger")?.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="popover" ref={rootRef}>
      <Button
        variant="secondary"
        size="sm"
        icon={icon}
        className="popover__trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
        {badge != null && badge > 0 && <span className="popover__badge">{badge}</span>}
      </Button>
      {open && (
        <div
          className={`popover__panel popover__panel--${align}${panelClassName ? ` ${panelClassName}` : ""}`}
          role="dialog"
          aria-label={panelLabel ?? label}
        >
          {typeof children === "function" ? children(() => setOpen(false)) : children}
        </div>
      )}
    </div>
  );
}
